import { forwardRef, CSSProperties } from 'react';

import MUITypography, {
  type TypographyProps as MUITypographyProps,
} from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import get from 'lodash/get';

import { Tokens } from '../Theme';
import type { DeepKeys } from '../types';

import { useStyles } from './Typography.styles';

export type TypographyColor =
  | DeepKeys<Tokens['colors']>
  | 'inherit'
  | undefined;

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'body3'
  | 'caption'
  | 'button'
  | 'overline'
  | 'inherit';

/**
 * Свойства компонента {@link Typography}
 */
export type TypographyProps = Pick<
  MUITypographyProps,
  'children' | 'ref' | 'className' | 'onClick' | 'sx' | 'display'
> & {
  /**
   * Флаг, указывающий, что текст должен быть оформлен как параграф.
   */
  paragraph?: boolean;

  /**
   * Флаг, указывающий, что текст не должен переноситься.
   */
  noWrap?: boolean;

  /**
   * Флаг, добавляющий отступ снизу.
   */
  gutterBottom?: boolean;

  /**
   * Выравнивание текста.
   */
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';

  /**
   * CSS свойство display.
   */
  display?: CSSProperties['display'];

  /**
   * Вариант стиля типографики.
   */
  variant?: TypographyVariant;

  /**
   * Цвет текста. Может быть ключом из цветов темы или 'inherit'.
   */
  color?: TypographyColor;

  /**
   * HTML-элемент, используемый для рендеринга.
   */
  component?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';

  /**
   * Значение для CSS свойства lineHeight.
   */
  lineHeight?: number;

  /**
   * Значение для CSS свойства whiteSpace.
   */
  whiteSpace?: CSSProperties['whiteSpace'];

  /**
   * Максимальное количество строк для обрезки текста.
   */
  maxLinesCount?: number;
  wordBreak?: CSSProperties['wordBreak'];
};

export const variantMapping = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'div',
  body2: 'div',
};

/**
 * Компонент типографики, который расширяет компонент Material-UI Typography дополнительными стилями и опциями цвета.
 *
 * @public
 * @param variant - Вариант стиля типографики.
 * @param color - Цвет текста. Может быть ключом из цветов темы или 'inherit'.
 * @param className - Дополнительные CSS-классы для применения к компоненту типографики.
 * @param lineHeight - Значение для CSS свойства lineHeight.
 * @param whiteSpace - Значение для CSS свойства whiteSpace.
 * @param maxLinesCount - Максимальное количество строк для обрезки текста.
 * @param rest - Остальные свойства, передаваемые в {@link MUITypography}.
 * @returns Компонент Typography.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link TypographyProps}
 */
export const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant = 'body1',
      color = 'text.light.primary',
      className,
      lineHeight,
      whiteSpace,
      wordBreak,
      maxLinesCount,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const { classes } = useStyles({ lineHeight, maxLinesCount });
    const body3 = classes.body3;

    return (
      <MUITypography
        {...rest}
        ref={ref}
        variant={variant !== 'body3' ? variant : 'body2'}
        color="inherit"
        variantMapping={variantMapping}
        classes={classes}
        className={classNames(className, {
          [body3]: variant === 'body3',
          [`aqa_${variant}`]: true,
          [classes.lineHeight]: true,
          [classes.trimLines]: !!maxLinesCount,
        })}
        whiteSpace={whiteSpace}
        style={{
          wordBreak,
          color:
            color === 'inherit' ? 'inherit' : get(theme.tokens.colors, color),
        }}
      />
    );
  },
);
